import { useState } from 'react';
import { REPORTE_CATALOG, REPORTE_IDS, type ReporteId } from '@lidotel/shared';
import { api } from '../lib/api.js';
import { ReportePicker } from '../components/ReportePicker.js';

/**
 * Reportes: un reporte a la medida con los bloques que se marquen —la misma
 * lista seleccionable que el dashboard— en Excel o en PDF, y debajo cada bloque
 * suelto en los dos formatos para quien solo quiere uno.
 *
 * Los dos formatos salen del MISMO cálculo en el Worker (`reunirCompleto`), así
 * que no pueden traer cifras distintas: el PDF no es un resumen del Excel, es el
 * mismo reporte con todos sus KPIs.
 */

export function Reportes() {
  const today = new Date().toISOString().slice(0, 10);
  const primeroDeMes = `${today.slice(0, 8)}01`;
  const [from, setFrom] = useState(primeroDeMes);
  const [to, setTo] = useState(today);
  const [semana, setSemana] = useState(today);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [bloques, setBloques] = useState<ReporteId[]>([...REPORTE_IDS]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(kind: string, fn: () => Promise<void>) {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al exportar');
    } finally {
      setBusy(null);
    }
  }

  const label = (k: string, txt: string) => (busy === k ? 'Generando…' : txt);
  const btn =
    'rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500 disabled:opacity-50';
  const btnAlt =
    'rounded-md border border-slate-600 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50';
  const card = 'rounded-xl border border-slate-700 bg-slate-800/60 p-4';
  const input =
    'mt-1 block rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100';

  // Los bloques de grano DÍA usan el rango; el resto va por año. Se pregunta al
  // catálogo en vez de enumerarlos, para que añadir uno nuevo no se olvide.
  const elegidos = REPORTE_CATALOG.filter((r) => bloques.includes(r.id));
  const conRango = elegidos.filter((r) => r.periodo === 'rango');
  const usaRango = conRango.length > 0;

  return (
    <div className="space-y-5">
      <h1 className="text-lg font-semibold text-slate-100">Reportes</h1>

      {/* ---------------- Reporte completo (a la medida) ---------------- */}
      <div className={card}>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-medium text-slate-200">Reporte completo</h2>
          <ReportePicker elegidos={bloques} onChange={setBloques} />
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Un solo documento con los bloques que marques y todos sus KPIs: en Excel, una hoja por
          bloque; en PDF, una portada con lo que lleva y detrás cada bloque. Los dos salen del mismo
          modelo que los paneles del dashboard, así que la descarga y la pantalla no pueden
          discrepar.
        </p>

        <p className="mb-3 text-xs text-slate-400">
          {elegidos.length === 0 ? (
            <span className="text-amber-400">
              Sin bloques marcados: elige al menos uno en «Bloques».
            </span>
          ) : (
            <>Va a llevar: {elegidos.map((r) => r.label).join(' · ')}.</>
          )}
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-400">
            Año
            <input
              type="number"
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
              className={`${input} w-28`}
            />
          </label>
          <label className={`text-xs ${usaRango ? 'text-slate-400' : 'text-slate-600'}`}>
            Desde
            <input
              type="date"
              value={from}
              disabled={!usaRango}
              onChange={(e) => setFrom(e.target.value)}
              className={`${input} disabled:opacity-40`}
            />
          </label>
          <label className={`text-xs ${usaRango ? 'text-slate-400' : 'text-slate-600'}`}>
            Hasta
            <input
              type="date"
              value={to}
              disabled={!usaRango}
              onChange={(e) => setTo(e.target.value)}
              className={`${input} disabled:opacity-40`}
            />
          </label>
          <button
            onClick={() => run('completo', () => api.exportCompleto(anio, bloques, from, to))}
            disabled={busy !== null || bloques.length === 0}
            className={btn}
          >
            {label('completo', 'Descargar Excel')}
          </button>
          <button
            onClick={() =>
              run('completo-pdf', () => api.exportCompletoPdf(anio, bloques, from, to))
            }
            disabled={busy !== null || bloques.length === 0}
            className={btnAlt}
          >
            {label('completo-pdf', 'Descargar PDF')}
          </button>
        </div>
        {!usaRango && (
          <p className="mt-2 text-[11px] text-slate-500">
            El rango solo lo usan los bloques con grano de día (tipos de habitación, rate codes y
            movimiento); los demás van por año.
          </p>
        )}
      </div>

      {/* ---------------- Libro semanal ---------------- */}
      <div className={card}>
        <h2 className="mb-1 text-sm font-medium text-slate-200">Libro semanal (Excel, 7 hojas)</h2>
        <p className="mb-3 text-xs text-slate-500">
          Va aparte del reporte completo porque su período es una SEMANA, no un año: una hoja por
          día, de lunes a domingo.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-400">
            Lunes de la semana
            <input
              type="date"
              value={semana}
              onChange={(e) => setSemana(e.target.value)}
              className={input}
            />
          </label>
          <button
            onClick={() => run('weekly', () => api.exportWeekly(semana))}
            disabled={busy !== null}
            className={btn}
          >
            {label('weekly', 'Descargar libro semanal')}
          </button>
        </div>
      </div>

      {/* ---------------- Bloques sueltos ---------------- */}
      <div className={card}>
        <h2 className="mb-1 text-sm font-medium text-slate-200">Bloques sueltos</h2>
        <p className="mb-3 text-xs text-slate-500">
          El mismo contenido que en el reporte combinado, pero un archivo por bloque y en el formato
          que prefieras.
        </p>

        <div className="space-y-3">
          {REPORTE_CATALOG.map((r) => (
            <div
              key={r.id}
              className="flex flex-wrap items-center gap-3 border-t border-slate-700/60 pt-3 first:border-0 first:pt-0"
            >
              <div className="min-w-0 flex-1">
                <span className="text-sm text-slate-200">{r.label}</span>
                <span className="ml-2 text-[11px] text-slate-500">
                  {r.periodo === 'rango' ? 'por rango de fechas' : `año ${anio}`}
                </span>
              </div>
              <button
                onClick={() =>
                  run(`${r.id}-x`, () => {
                    if (r.id === 'totalizacion') return api.exportMonthly(anio);
                    if (r.id === 'noches') return api.exportLibroNoches(anio);
                    if (r.id === 'cuentas') return api.exportCuentas(anio);
                    if (r.id === 'canales') return api.exportCanales(anio);
                    if (r.id === 'expedia') return api.exportExpedia(anio);
                    if (r.id === 'markets') return api.exportMarkets(from, to);
                    if (r.id === 'operacion') return api.exportOperacion(from, to);
                    return api.exportHabitaciones(from, to);
                  })
                }
                disabled={busy !== null}
                className={btn}
              >
                {label(`${r.id}-x`, 'Excel')}
              </button>
              <button
                onClick={() =>
                  run(`${r.id}-p`, () => {
                    if (r.id === 'totalizacion') return api.exportMonthlyPdf(anio);
                    if (r.id === 'noches') return api.exportLibroNochesPdf(anio);
                    if (r.id === 'cuentas') return api.exportCuentasPdf(anio);
                    if (r.id === 'canales') return api.exportCanalesPdf(anio);
                    if (r.id === 'expedia') return api.exportExpediaPdf(anio);
                    if (r.id === 'markets') return api.exportMarketsPdf(from, to);
                    if (r.id === 'operacion') return api.exportOperacionPdf(from, to);
                    return api.exportHabitacionesPdf(from, to);
                  })
                }
                disabled={busy !== null}
                className={btnAlt}
              >
                {label(`${r.id}-p`, 'PDF')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {error && <p className="rounded-lg bg-rose-950/60 px-3 py-2 text-rose-300">{error}</p>}
    </div>
  );
}
